/* Size the visible character, not the transparent image canvas. */
(() => {
  if (window.UniverseBeanCharacterFit) return;
  // Shared by the homepage, sidebar, download and standalone conversations.
  const worldScript = document.createElement('script');
  worldScript.src = '/chat-world.js'; worldScript.defer = true; document.head.append(worldScript);
  // Shipped rabbit_1_245.webp: alpha > 16 bounds.
  const reference = { width: 689, height: 1484, left: 99, top: 25, right: 591, bottom: 1460 };
  // Visible head-to-foot height / complete dialog height, from the seven-class reference.
  const categoryHeights = { '小IP': .40, 'IP': .46, '小孩': .51, '少年': .57, '成年': .63, '大身高': .68, '巨大身高': .80 };
  let catalogBounds;
  const loadCatalogBounds = () => {
    if (!catalogBounds) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 6000);
      catalogBounds = fetch('/bean-character-bounds.json?v=20260914-front-actions1', {
        credentials: 'same-origin', cache: 'no-cache', signal: controller.signal
      })
        .then(response => response.ok ? response.json() : {})
        .catch(() => ({}))
        .finally(() => clearTimeout(timeout));
    }
    return catalogBounds;
  };
  // Same-origin dialogs share one bounded queue and completed measurements.
  // Closing one consumer must not cancel a measurement another dialog needs.
  const workerUrl = new URL('bean-character-bounds-worker.js', (document.currentScript && document.currentScript.src) || document.baseURI).href;
  let broker;
  function boundsBroker() {
    if (broker) return broker;
    let host = window;
    try { while (host.parent !== host && host.parent.location.origin === location.origin) host = host.parent; } catch (_) {}
    if (host.__beanBoundsBroker) return broker = host.__beanBoundsBroker;
    const tasks = new Map(), completed = new Map(), suspended = new Set();
    const stats = {started: 0, completed: 0, cancelled: 0, reused: 0};
    let worker = null, active = null, serial = 0, disabled = false;
    function finish(task, result) {
      if (tasks.get(task.url) !== task) return;
      host.clearTimeout(task.timer);
      tasks.delete(task.url);
      if (active === task) active = null;
      if (result && result.bounds) {
        completed.set(task.url, result); stats.completed++;
        if (completed.size > 256) completed.delete(completed.keys().next().value);
      }
      task.resolve(result);
      pump();
    }
    function pump() {
      if (active || !tasks.size) return;
      const task = [...tasks.values()].find(item => [...item.owners].some(owner => !suspended.has(owner)));
      if (!task) return;
      active = task;
      if (disabled) { finish(task, {unsupported: true}); return; }
      try {
        if (!worker) {
          const instance = worker = new host.Worker(workerUrl);
          instance.onmessage = ({data}) => { if (worker === instance && active && active.id === data.id) finish(active, data); };
          instance.onerror = () => {
            if (worker !== instance) return;
            disabled = true; worker.terminate(); worker = null;
            if (active) finish(active, {unsupported: true});
          };
        }
        task.timer = host.setTimeout(() => {
          worker.terminate(); worker = null; finish(task, {bounds: null});
        }, 12000);
        stats.started++;
        worker.postMessage({id: task.id, url: task.url});
      } catch (_) { disabled = true; finish(task, {unsupported: true}); }
    }
    broker = host.__beanBoundsBroker = {
      measure(url, owner) {
        if (completed.has(url)) { stats.reused++; return Promise.resolve(completed.get(url)); }
        let task = tasks.get(url);
        if (task) { task.owners.add(owner); stats.reused++; pump(); return task.promise; }
        task = {url, id: ++serial, owners: new Set([owner])};
        task.promise = new Promise(resolve => { task.resolve = resolve; });
        tasks.set(url, task); pump(); return task.promise;
      },
      release(owner, dispose = false) {
        suspended.add(owner);
        if (dispose) {
          for (const task of [...tasks.values()]) {
            task.owners.delete(owner);
            if (!task.owners.size) {
              tasks.delete(task.url); host.clearTimeout(task.timer);
              if (active === task) { worker && worker.terminate(); worker = null; active = null; }
              task.resolve({bounds: null});
            }
          }
          suspended.delete(owner);
        }
        if (active && [...active.owners].every(item => suspended.has(item))) {
          // Keep the promise pending while hidden; resuming must not use fake full-canvas bounds.
          host.clearTimeout(active.timer); worker && worker.terminate(); worker = null;
          active = null; stats.cancelled++;
        }
        pump();
      },
      resume(owner) { suspended.delete(owner); pump(); },
      inspect() { return Object.assign({}, stats, {pending: tasks.size, cached: completed.size, active: active ? 1 : 0}); }
    };
    return broker;
  }
  let measurementPaused = false;
  const suspend = () => { measurementPaused = true; if (broker) broker.release(window); };
  const resume = () => { measurementPaused = false; if (broker) broker.resume(window); };
  window.addEventListener('pagehide', () => { if (broker) broker.release(window, true); });
  document.addEventListener('visibilitychange', () => { if (document.hidden) suspend(); else if ((!document.querySelector('#bean-chat-overlay') || document.querySelector('#bean-chat-overlay').dataset.beanHostOpen !== 'false')) resume(); });
  const cache = new Map();
  const isAnimated = bytes => {
    const tag = offset => String.fromCharCode(...bytes.subarray(offset, offset + 4));
    if (tag(0).startsWith('GIF')) return true;
    if (tag(8) === 'WEBP') {
      for (let offset = 12; offset + 8 <= bytes.length;) {
        const size = new DataView(bytes.buffer, bytes.byteOffset + offset + 4, 4).getUint32(0, true);
        if (tag(offset) === 'ANIM' || tag(offset) === 'ANMF') return true;
        offset += 8 + size + (size % 2);
      }
    }
    if (bytes[0] === 137 && tag(1) === 'PNG\r') {
      for (let offset = 8; offset + 8 <= bytes.length;) {
        const size = new DataView(bytes.buffer, bytes.byteOffset + offset, 4).getUint32(0);
        if (tag(offset + 4) === 'acTL') return true;
        offset += 12 + size;
      }
    }
    return false;
  };
  const localBounds = new Map();
  const localPreviews = new Set();
  const previewLayoutBounds = new Map();
  const staticPreviewBounds = new Map();
  let indexedCatalog;
  function indexCatalog() {
    if (!window.BeanChatLocalMedia || indexedCatalog === window.BeanChatLocalMedia) return;
    indexedCatalog = window.BeanChatLocalMedia;
  Object.entries((window.BeanChatLocalMedia && window.BeanChatLocalMedia.media) || {}).forEach(([url, entry]) => {
    if (entry.kind !== 'character') return;
    localBounds.set(new URL(url, document.baseURI).href, entry.bounds);
    // A static preview is one pose; the HD animation union includes other poses.
    // Measure its own alpha pixels rather than counting that transparent union.
    localPreviews.add(new URL(entry.preview, document.baseURI).href);
    previewLayoutBounds.set(new URL(entry.preview, document.baseURI).href, entry.bounds);
  });
  ((window.BeanChatLocalMedia && window.BeanChatLocalMedia.profiles) || []).forEach(profile => {
    if (profile.staticPreviewUrl) {
      const url = new URL(profile.staticPreviewUrl, document.baseURI).href;
      localPreviews.add(url);
      if (profile.staticPreviewBounds) {
        previewLayoutBounds.set(url, profile.staticPreviewBounds);
        staticPreviewBounds.set(url, profile.staticPreviewBounds);
      }
    }
    const entry = window.BeanChatLocalMedia.media[profile.hdUrl];
    if (profile.hdImageUrl && entry) localBounds.set(new URL(profile.hdImageUrl, document.baseURI).href, entry.bounds);
  });
  }
  const measure = url => {
    indexCatalog();
    const requested = new URL(url, document.baseURI);
    const original = requested.origin === location.origin && requested.pathname === '/api/universe-bean/media'
      ? requested.searchParams.get('url') : requested.href;
    const isLocalPreview = localPreviews.has(original);
    // These bounds were measured from this exact static image during generation.
    // Re-fetching it and scanning its pixels here delays the first visible pose.
    if (staticPreviewBounds.has(original)) return Promise.resolve(staticPreviewBounds.get(original));
    const preparedChat = !isLocalPreview && localBounds.get(original);
    if (preparedChat) return Promise.resolve(preparedChat);
    const prepared = !isLocalPreview && window.WebsiteMedia && window.WebsiteMedia.bounds(url);
    if (prepared) return Promise.resolve(prepared);
    if (url === new URL('/assets/products/2智体/rabbit_1_245.webp', document.baseURI).href) return Promise.resolve(reference);
    if (cache.has(url)) return cache.get(url);
    const pending = (isLocalPreview ? Promise.resolve({}) : loadCatalogBounds()).then(async known => {
      const saved = known[original];
      if (saved && saved.width > 0 && saved.height > 0 &&
          saved.left >= 0 && saved.top >= 0 && saved.right > saved.left &&
          saved.bottom > saved.top && saved.right <= saved.width && saved.bottom <= saved.height) return saved;
      if (typeof Worker === 'function') {
        const shared = boundsBroker();
        if (measurementPaused) shared.release(window);
        const measured = await shared.measure(requested.href, window);
        if (!measured.unsupported) return measured.bounds;
      }
      return new Promise(resolve => {
        const probe = new Image();
        const controller = new AbortController();
        let objectUrl = '';
        let animated = false;
        let decoder;
        let settled = false;
        const finish = bounds => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          controller.abort();
          if (decoder) { decoder.close(); decoder = null; }
          probe.onload = probe.onerror = null;
          if (objectUrl) URL.revokeObjectURL(objectUrl);
          resolve(bounds);
        };
        const timer = setTimeout(() => finish(null), 12000);
        probe.onload = () => {
          try {
            // Unsupported animated formats use the profile's calibrated static bounds.
            if (animated) {
              finish(null);
              return;
            }
            const ratio = Math.min(1, 1024 / Math.max(probe.naturalWidth, probe.naturalHeight));
            const canvas = document.createElement('canvas');
            canvas.width = Math.max(1, Math.round(probe.naturalWidth * ratio));
            canvas.height = Math.max(1, Math.round(probe.naturalHeight * ratio));
            const context = canvas.getContext('2d', { willReadFrequently: true });
            context.drawImage(probe, 0, 0, canvas.width, canvas.height);
            const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
            let left = canvas.width; let top = canvas.height; let right = 0; let bottom = 0;
            for (let y = 0; y < canvas.height; y += 1) {
              for (let x = 0; x < canvas.width; x += 1) {
                if (pixels[(y * canvas.width + x) * 4 + 3] <= 16) continue;
                left = Math.min(left, x); top = Math.min(top, y);
                right = Math.max(right, x + 1); bottom = Math.max(bottom, y + 1);
              }
            }
            const xScale = probe.naturalWidth / canvas.width;
            const yScale = probe.naturalHeight / canvas.height;
            finish(right > left && bottom > top ? {
              width: probe.naturalWidth, height: probe.naturalHeight,
              left: left * xScale, top: top * yScale, right: right * xScale, bottom: bottom * yScale
            } : null);
          } catch (_) { finish(null); }
        };
        probe.onerror = () => finish(null);
        fetch(url, { mode: 'cors', credentials: 'omit', signal: controller.signal })
          .then(response => {
            if (!response.ok) throw new Error('Character pixels unavailable');
            return response.blob();
          })
          .then(async blob => {
            if (settled) return;
            const bytes = new Uint8Array(await blob.arrayBuffer());
            animated = isAnimated(bytes);
            if (settled) return;
            if (animated && typeof ImageDecoder === 'function') {
              const type = String.fromCharCode(...bytes.subarray(8, 12)) === 'WEBP' ? 'image/webp' : blob.type;
              decoder = new ImageDecoder({ data: bytes, type, preferAnimation: true });
              await decoder.tracks.ready;
              if (settled) return;
              const count = decoder.tracks.selectedTrack.frameCount;
              const canvas = document.createElement('canvas');
              const context = canvas.getContext('2d', { willReadFrequently: true });
              let union = null;
              for (let index = 0; index < count && !settled; index++) {
                const frame = (await decoder.decode({ frameIndex: index })).image;
                try {
                  if (settled) return;
                  const width = frame.displayWidth, height = frame.displayHeight;
                  const ratio = Math.min(1, 320 / Math.max(width, height));
                  canvas.width = Math.max(1, Math.round(width * ratio));
                  canvas.height = Math.max(1, Math.round(height * ratio));
                  context.drawImage(frame, 0, 0, canvas.width, canvas.height);
                  const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
                  let left = canvas.width, top = canvas.height, right = 0, bottom = 0;
                  for (let y = 0; y < canvas.height; y++) {
                    for (let x = 0; x < canvas.width; x++) {
                      if (pixels[(y * canvas.width + x) * 4 + 3] <= 16) continue;
                      left = Math.min(left, x); top = Math.min(top, y);
                      right = Math.max(right, x + 1); bottom = Math.max(bottom, y + 1);
                    }
                  }
                  if (right > left && bottom > top) {
                    const next = { width, height, left: left * width / canvas.width, top: top * height / canvas.height,
                      right: right * width / canvas.width, bottom: bottom * height / canvas.height };
                    union = union ? { width, height, left: Math.min(union.left, next.left), top: Math.min(union.top, next.top),
                      right: Math.max(union.right, next.right), bottom: Math.max(union.bottom, next.bottom) } : next;
                  }
                } finally { frame.close(); }
                if (index % 8 === 7) await new Promise(resolve => setTimeout(resolve, 0));
              }
              finish(union);
              return;
            }
            objectUrl = URL.createObjectURL(blob);
            probe.src = objectUrl;
          })
          .catch(() => finish(null));
      });
    });
    cache.set(url, pending);
    pending.then(bounds => { if (!bounds && cache.get(url) === pending) cache.delete(url); });
    if (cache.size > 80) cache.delete(cache.keys().next().value);
    return pending;
  };

  // Prepare offscreen and choose once. A late HD response is cached, never swapped into a visible pose.
  const chooseSource = profile => {
    const load = (url, phase) => new Promise(resolve => {
      if (!url) { resolve(null); return; }
      const image = new Image();
      let settled = false;
      const finish = result => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        image.onload = image.onerror = null;
        resolve(result);
      };
      const timer = setTimeout(() => { finish(null); image.src = ''; }, 12000);
      image.onload = () => {
        const decoded = typeof image.decode === 'function' ? image.decode() : Promise.resolve();
        decoded.then(() => finish({ url, phase }), () => finish(null));
      };
      image.onerror = () => finish(null);
      image.src = url;
    });
    const preferred = profile.hdUrl || profile.image;
    if (window.WebsiteMedia && window.WebsiteMedia.has(preferred) && window.WebsitePerformance.mode !== 'high') {
      return load(window.WebsiteMedia.url(preferred), 'lightweight');
    }
    const previewUrl = profile.previewUrl || profile.image;
    const preview = load(previewUrl, 'preview');
    if (!profile.hdUrl || profile.hdUrl === previewUrl) return preview;
    const hd = load(profile.hdUrl, 'hd');
    const previewReady = preview.then(async result => {
      if (!result) return hd;
      let timer;
      const grace = new Promise(resolve => { timer = setTimeout(() => resolve(null), 500); });
      const preferred = await Promise.race([hd, grace]);
      clearTimeout(timer);
      return preferred || result;
    });
    return Promise.race([previewReady, hd.then(result => result || previewReady)]);
  };
  const controllers = new WeakMap();
  const actionAnchor = (head, url) => {
    const match = /(?:^|\/)rabbit_(\d+(?:_[^/?#.]*)?)\.webp(?:[?#]|$)/i.exec(url || '');
    const actionId = match && match[1];
    if (!actionId) return null;
    const entry = (Array.isArray(head) ? head : []).find(item => (String(item.id) === actionId
      || (/^\d+$/.test(String(item.id)) && /^\d+$/.test(actionId) && Number(item.id) === Number(actionId)))
      && (!item.action_url || item.action_url.split(/[?#]/)[0] === url.split(/[?#]/)[0]));
    const values = String(entry && entry.value || '').split(',').map(value => value.trim() === '' ? NaN : Number(value));
    return values.length === 4 && values.every(value => Number.isFinite(value) && value >= 0 && value <= 100)
      ? { startX: values[0] / 100, endX: values[2] / 100, groundY: Math.max(values[1], values[3]) / 100 } : null;
  };
  const begin = overlay => {
    const art = overlay && overlay.querySelector('.bean-chat-agent-art');
    const controller = art && controllers.get(art);
    if (controller) controller.begin();
  };
  const setSource = (art, url, profile = {}) => {
    const controller = controllers.get(art);
    if (controller) controller.setSource(url, profile);
    else art.src = url;
  };

  const install = overlay => {
    const art = overlay && overlay.querySelector('.bean-chat-agent-art');
    if (!art) return () => {};
    const stage = art.parentElement;
    const thread = overlay.querySelector('.bean-chat-agent-main');
    let bounds = null;
    let ready = false;
    let holding = false;
    let serial = 0;
    let sourceSerial = 0;
    let disposed = false;
    let frame = 0;
    let characterProfile = {};
    let lastPlacement = null;
    let anchorRatio = null;
    const reset = () => {
      ['position', 'width', 'height', 'left', 'top', 'max-width', '--fit-width', '--fit-height', '--fit-left', '--fit-top'].forEach(key => art.style.removeProperty(key));
      delete art.dataset.characterFit;
    };
    const conceal = () => {
      art.style.visibility = 'hidden';
      stage.setAttribute('aria-busy', 'true');
    };
    let actionHold = null;
    const clearActionHold = () => { if(actionHold)actionHold.remove(); actionHold = null; };
    const reveal = () => {
      clearActionHold();
      art.style.removeProperty('visibility');
      stage.removeAttribute('aria-busy');
    };
    const fit = () => {
      frame = 0;
      if (disposed || holding || !ready || !art.complete || !art.naturalWidth) return;
      // The fixed presenter is hidden in scene layouts. Their full-width content
      // column must not overwrite its left/right placement or speaking anchor.
      if (overlay.dataset.agentStructure && overlay.dataset.agentStructure !== 'horizontal') return;
      if (!bounds) {
        art.dataset.characterFit = 'contain-fallback';
        reveal();
        return;
      }
      const width = stage.clientWidth; const height = stage.clientHeight;
      if (!width || !height) return;
      // Match the default's original visible height and foot position in each layout.
      const referenceScale = Math.min(width / reference.width, height / reference.height);
      const targetHeight = (reference.bottom - reference.top) * referenceScale;
      const baseline = height - (reference.height - reference.bottom) * referenceScale;
      const mobileLayout = document.body.hasAttribute('data-bean-mobile') || document.documentElement.hasAttribute('data-mobile-app');
      const promoLayout = document.documentElement.hasAttribute('data-promo-player');
      overlay.classList.add('ground-scene');
      overlay.classList.toggle('mobile-ground-scene', mobileLayout);
      const characterLog = art.closest('.bean-chat-log');
      if (characterLog) characterLog.style.setProperty('overflow', 'visible', 'important');
      const safeLeft = Math.max(0, -stage.offsetLeft);
      // Mobile copy is full-width with left padding; its offset is not the character boundary.
      const safeRight = mobileLayout || promoLayout ? width : Math.min(width, thread ? thread.offsetLeft - stage.offsetLeft - 6 : width);
      const availableWidth = Math.max(1, safeRight - safeLeft);
      // Preserve the pre-minimum layout exactly, including a preview's prior
      // canvas placement. Actual alpha bounds are used only to decide whether
      // that visible pose is too short, and to enlarge it around its old anchors.
      const layoutBounds = previewLayoutBounds.get(art.src) || bounds;
      const canvasScale = Math.min(targetHeight / (layoutBounds.bottom - layoutBounds.top), availableWidth / (layoutBounds.right - layoutBounds.left));
      const fittedWidth = (layoutBounds.right - layoutBounds.left) * canvasScale;
      const fittedLeft = Math.max(safeLeft, Math.min((width - fittedWidth) / 2, safeRight - fittedWidth));
      const fittedScale = canvasScale * Math.min(layoutBounds.width / bounds.width, layoutBounds.height / bounds.height);
      const fittedCenter = fittedLeft - layoutBounds.left * canvasScale + (layoutBounds.width * canvasScale - bounds.width * fittedScale) / 2 + (bounds.left + bounds.right) / 2 * fittedScale;
      const fittedBaseline = baseline + (layoutBounds.height - layoutBounds.bottom) * canvasScale - (bounds.height - bounds.bottom) * fittedScale;
      // Reference red line: 41% down the dialog, in local layout coordinates.
      // This also works inside the scaled homepage iframe and in fullscreen.
      const overlayRect = overlay.getBoundingClientRect();
      const stageRect = stage.getBoundingClientRect();
      const layoutScale = overlayRect.height / overlay.offsetHeight || 1;
      const minimumTop = (overlayRect.top + overlayRect.height * .41 - stageRect.top) / layoutScale;
      const minimumHeight = Math.max(0, Math.min(fittedBaseline, fittedBaseline - minimumTop));
      const category = String(characterProfile.sub_category || '').trim();
      const categoryHeight = categoryHeights[category];
      const desiredScale = categoryHeight
        ? overlay.offsetHeight * categoryHeight / (bounds.bottom - bounds.top)
        : Math.max(fittedScale, minimumHeight / (bounds.bottom - bounds.top));
      // The 24% column is a text gutter, not a limit on the character's height.
      const drawingRight = mobileLayout ? (overlayRect.right - stageRect.left) / layoutScale : safeRight;
      const mediaScale = window.AgentFrameMedia ? window.AgentFrameMedia.state.agent.scale : 1;
      const scaledDesired = desiredScale * mediaScale;
      const standing = characterProfile.staticPreviewBounds || bounds;
      const standingAspect = (standing.right-standing.left)/Math.max(1,standing.bottom-standing.top);
      const phonePromo = promoLayout && matchMedia('(max-width:720px), (pointer:coarse) and (max-width:960px)').matches;
      const stableHeight = phonePromo ? Math.min(scaledDesired*(bounds.bottom-bounds.top),height,overlay.offsetHeight*.62,overlay.offsetWidth*.48/standingAspect) : mobileLayout && !promoLayout ? Math.min(scaledDesired*(bounds.bottom-bounds.top),(drawingRight-safeLeft)/standingAspect) : scaledDesired*(bounds.bottom-bounds.top);
      const scale = stableHeight/Math.max(1,bounds.bottom-bounds.top);
      const visibleWidth = (bounds.right - bounds.left) * scale;
      const offset = Number(characterProfile.right_offset);
      const rightShift = Number.isFinite(offset) ? overlay.offsetWidth * offset / 100 : 0;
      // Preserve the previous horizontal center and the original foot baseline.
      const visibleLeft = fittedCenter - visibleWidth / 2 + rightShift;
      art.style.position = 'absolute';
      art.style.maxWidth = 'none';
      art.style.width = `${bounds.width * scale}px`;
      art.style.height = `${bounds.height * scale}px`;
      art.style.left = `${visibleLeft - bounds.left * scale}px`;
      const anchor = characterProfile.speakingAnchor;
      const canvasWidth = bounds.width * scale;
      const defaultLeft = visibleLeft - bounds.left * scale;
      let anchoredLeft = anchor
        ? (anchorRatio === null ? fittedCenter + rightShift : anchorRatio * width) - canvasWidth * anchor.startX
        : defaultLeft;
      let mobileCenterLayout = mobileLayout;
      try {
        const host = window.top.document;
        mobileCenterLayout = mobileCenterLayout || host.body.hasAttribute('data-bean-mobile') || host.documentElement.hasAttribute('data-mobile-landscape');
      } catch (_) { /* Cross-origin embeds retain their own layout classification. */ }
      if (mobileCenterLayout) {
        // Every mobile character shares the red reference center, regardless of pose width.
        // Actions use their calibrated body center; static previews use visible alpha bounds.
        const leftEdge = (overlayRect.left - stageRect.left) / layoutScale;
        const center = leftEdge + overlay.offsetWidth * .13;
        anchoredLeft = center - (anchor ? canvasWidth * anchor.startX : (bounds.left + bounds.right) / 2 * scale);
      }
      // All poses of one presenter share the same body centre and visible
      // height. Alpha bounds vary between actions, so right-edge alignment
      // would make the same character jump horizontally on every swap.
      if (promoLayout) {
        const bodyCenter = anchor ? canvasWidth * anchor.startX : (bounds.left + bounds.right) / 2 * scale;
        anchoredLeft = (phonePromo ? stableHeight * standingAspect / 2 : width * .55) - bodyCenter;
      }
      art.style.left = `${anchoredLeft}px`;
      lastPlacement = {
        key: characterProfile.key,
        endRatio: (anchoredLeft + (anchor ? canvasWidth * anchor.endX : (bounds.left + bounds.right) / 2 * scale)) / width
      };
      art.dataset.actionAnchorX = anchor ? String(anchoredLeft + canvasWidth * anchor.startX) : '';
      const groundY = anchor && Number.isFinite(anchor.groundY) ? anchor.groundY * bounds.height : bounds.bottom;
      // All hosts share the inside bottom border as the ground. The composer
      // overlays this scene; its height is never part of the foot baseline.
      const groundInset = parseFloat(getComputedStyle(overlay).borderBottomWidth) || 0;
      const groundBaseline = promoLayout ? height : (overlayRect.bottom - stageRect.top) / layoutScale - groundInset;
      art.style.top = `${groundBaseline - groundY * scale}px`;
      // Mobile layouts use !important dimensions; supply the same computed fit.
      ['width', 'height', 'left', 'top'].forEach(key => art.style.setProperty('--fit-' + key, art.style[key]));
      art.dataset.characterFit = 'visible-bounds';
      art.__beanVisiblePlacement = {key:characterProfile.key,bounds:Object.assign({},bounds)};
      reveal();
    };
    const schedule = () => {
      if (!disposed && !frame) frame = requestAnimationFrame(fit);
    };
    const refresh = () => {
      if (holding || disposed) return;
      const request = ++serial;
      const url = art.src;
      ready = false;
      bounds = null;
      conceal();
      reset();
      if (!url || !art.complete || !art.naturalWidth) return;
      measure(url).then(result => {
        if (disposed || holding || request !== serial || art.src !== url) return;
        bounds = result;
        const fallback = characterProfile.staticPreviewBounds;
        if (!bounds && fallback && fallback.width > 0 && fallback.height > 0) {
          const x = art.naturalWidth / fallback.width, y = art.naturalHeight / fallback.height;
          bounds = { width: art.naturalWidth, height: art.naturalHeight,
            left: fallback.left * x, right: fallback.right * x, top: fallback.top * y, bottom: fallback.bottom * y };
        }
        ready = true;
        // Size and visibility are committed together, before the browser can paint.
        fit();
      });
    };
    controllers.set(art, {
      begin() {
        clearActionHold();
        lastPlacement = null;
        anchorRatio = null;
        // An old request failing during selection must not release the new loading state.
        art.onerror = null;
        holding = true;
        ready = false;
        serial += 1;
        sourceSerial += 1;
        conceal();
      },
      setSource(url, profile) {
        const request = ++sourceSerial;
        const absolute = new URL(url, document.baseURI).href;
        // Keep the correctly sized static pose visible while preparing animation bounds.
        // Mobile decoders can clear the current <img> for a frame on src changes.
        // Keep its animated pixels at the existing placement until fit() reveals the new action.
        let retained = null;
        if(document.documentElement.dataset.deviceFamily==='mobile'&&!holding&&profile.key&&profile.key===characterProfile.key&&art.src!==absolute&&art.complete&&art.naturalWidth){
          clearActionHold();
          retained=art.cloneNode(false);
          for(const attribute of [...retained.attributes])if(attribute.name!=='src')retained.removeAttribute(attribute.name);
          const style=getComputedStyle(art);
          for(const key of ['position','width','height','left','top','right','bottom','max-width','max-height','object-fit','object-position','transform','transform-origin','opacity','z-index'])retained.style.setProperty(key,style.getPropertyValue(key));
          retained.style.pointerEvents='none';retained.style.visibility='hidden';retained.setAttribute('aria-hidden','true');
          art.after(retained);actionHold=retained;
        }
        const retainedReady=retained?retained.decode().catch(()=>{retained.remove();if(actionHold===retained)actionHold=null;}):Promise.resolve();
        Promise.all([measure(absolute),retainedReady]).then(() => {
          if (disposed || request !== sourceSerial) {if(retained)retained.remove();return;}
          if(retained&&retained.isConnected)retained.style.visibility='visible';
          anchorRatio = profile.speakingAnchor && lastPlacement && lastPlacement.key === profile.key ? lastPlacement.endRatio : null;
          characterProfile = profile;
          holding = false;
          conceal();
          if (art.src !== absolute) art.src = url;
          refresh();
        });
      }
    });
    art.addEventListener('load', refresh);
    art.addEventListener('error', refresh);
    const mutation = new MutationObserver(refresh);
    mutation.observe(art, { attributes: true, attributeFilter: ['src'] });
    const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(schedule) : null;
    if (resize) { resize.observe(overlay); resize.observe(stage); if (thread) resize.observe(thread); }
    const layout = new MutationObserver(changes => {
      if (changes.some(change => change.attributeName === 'data-agent-structure')) {
        lastPlacement = null;
        anchorRatio = null;
      }
      schedule();
    });
    layout.observe(overlay, { attributes: true, attributeFilter: ['class', 'hidden', 'data-agent-structure'] });
    window.addEventListener('resize', schedule);
    window.addEventListener('agent-frame:media-change', schedule);
    refresh();
    return () => {
      disposed = true;
      serial += 1;
      cancelAnimationFrame(frame);
      controllers.delete(art);
      reveal();
      mutation.disconnect(); layout.disconnect();
      if (resize) resize.disconnect();
      window.removeEventListener('resize', schedule);
      window.removeEventListener('agent-frame:media-change', schedule);
      art.removeEventListener('load', refresh);
      art.removeEventListener('error', refresh);
      reset();
    };
  };
  // Qualify the actual visible subject, not the file name or transparent canvas.
  const isHighResolution = (item, kind) => {
    const media = window.BeanChatLocalMedia && window.BeanChatLocalMedia.media[item && item.hdUrl];
    const b = media && media.bounds;
    if (!b || media.kind !== kind) return false;
    return kind === 'character'
      ? b.bottom - b.top >= 720 && b.right - b.left >= 160
      : b.width >= 1080 && b.height >= 1080;
  };
  const prepare = (url, bounds) => {
    if (bounds) cache.set(url, Promise.resolve(bounds));
    if (cache.size > 80) cache.delete(cache.keys().next().value);
    return measure(url);
  };
  window.UniverseBeanCharacterFit = { install, begin, setSource, chooseSource, isHighResolution, prepare, actionAnchor, suspend, resume, measurementStats: () => (broker && broker.inspect()) || {pending: 0, cached: 0} };
})();



//////////////////
// WEBPACK FOOTER
// ./homepage-source/bean-character-fit.js
// module id = NiLu
// module chunks = 1